/**
 * Stock on hand before waste is last stock plus receipts minus sold.
 * When waste is larger than that, expected quantity goes negative and a
 * variance percentage is not a result.
 */

const finite = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

export const stockOnHandBeforeWaste = product =>
  finite(product?.lastQuantity) +
  finite(product?.qtySinceLastStockTake) -
  finite(product?.sold)

/** Write both flags together so OTP and master list agree. */
export const stockCountEnteredFlags = entered => ({
  inStockEntered: Boolean(entered),
  inStockCounted: Boolean(entered),
})

/**
 * A stored 0 is the line's starting number, not a count.
 * Someone counting zero sets both flags. An older line whose
 * number is already above 0 was counted before those flags existed.
 */
export const stockInStockWasEntered = product => {
  if (product?.inStockEntered === true || product?.inStockCounted === true) {
    return true
  }
  if (product?.inStockEntered === false || product?.inStockCounted === false) {
    return false
  }
  const count = Number(product?.inStock)
  return Number.isFinite(count) && count > 0
}

/**
 * A stored 0 is the line's starting sold number, not an entry.
 * Typing a number, including 0, or a sold recipe filling the line, sets soldEntered.
 * An older line whose sold number is already above 0 was filled before that flag existed.
 */
export const stockSoldWasEntered = product => {
  if (product?.soldEntered === true) return true
  if (product?.soldEntered === false) return false
  const sold = Number(product?.sold)
  return Number.isFinite(sold) && sold > 0
}

/** A missing list figure stays missing. A real 0 is still 0. */
export const normalizeStockListExpected = value => {
  if (value === undefined || value === null || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** List cards: missing / 0 / negative expected is not a real £ figure. */
export const STOCK_LIST_EXPECTED_PLACEHOLDER = 'No figure yet'

/** Expected on a stock-list card. Placeholder when there is no honest £. */
export const stockListExpectedText = (value, format) => {
  const number = normalizeStockListExpected(value)
  if (number === undefined || number <= 0)
    return STOCK_LIST_EXPECTED_PLACEHOLDER
  return format(number)
}

export const stockLinesWereCounted = products =>
  Array.isArray(products) && products.some(stockInStockWasEntered)

/** List cards: empty Stock next to a fat Expected looks like a missing figure. */
export const STOCK_LIST_NOT_COUNTED_YET = 'Not counted yet'

/**
 * Stock on a stock-list card. Uncounted lines say so instead of a blank.
 * A count that includes a typed 0 still shows £0.00.
 * linesCounted is unknown until those lines have been read.
 */
export const stockListStockText = (value, linesCounted, format) => {
  const parsed = Number(value)
  const number = Number.isFinite(parsed) ? parsed : null
  if (linesCounted === false) return STOCK_LIST_NOT_COUNTED_YET
  if (number === null) return STOCK_LIST_NOT_COUNTED_YET
  if (number === 0 && linesCounted !== true) return STOCK_LIST_NOT_COUNTED_YET
  return format(number)
}

/**
 * Stock value, VAT, Net, and Gross. Blank until the line is counted.
 * A counted 0 still shows the formatted £0.00.
 */
export const stockLineMoneyLabel = (product, label) =>
  stockInStockWasEntered(product) ? String(label ?? '') : ''

export const wasteExceedsStockOnHand = product => {
  if (!product) return false
  const onHand = stockOnHandBeforeWaste(product)
  const wastage = finite(product.wastage)
  return onHand >= 0 && wastage > onHand
}
